import { useEffect, useState, type ReactNode } from "react";
import {
  Intercom,
  boot,
  show as showIntercom,
  onUnreadCountChange,
} from "@intercom/messenger-js-sdk";
import { IntercomContext } from "./IntercomContext";
import { shutdownIntercom } from "./shutdownIntercom";

type IntercomProviderProps = {
  children: ReactNode;
  appId: string;
  name: string;
  userId: string;
  token: string | undefined;
  isPending: boolean;
  isError: boolean;
};

export const IntercomProvider = ({
  children,
  appId,
  name,
  userId,
  token,
  isPending,
  isError,
}: IntercomProviderProps) => {
  const [unreadCount, setUnreadCount] = useState(0);

  // Runs on every render and is sent without identity verification, so it must
  // not carry anything about the user: on a shared device, the messenger can
  // still hold the previous user's session at this point, and a `name` sent
  // here would be written to that user's contact.
  Intercom({
    app_id: appId,
    hide_default_launcher: true,
    region: "eu",
    // If we include `user_id` here, boot becomes a no-op, and then we don't get the initial unread message count
    // user_id: userId,
  });

  useEffect(() => {
    if (token === undefined) {
      return;
    }
    // Used for:
    // 1. Associating the Intercom instance with the right user_id (could be done in the Intercom constructor, if not for:)
    // 2. Ensuring that we get the initial unread count right
    boot({
      app_id: appId,
      user_id: userId,
      name,
      region: "eu",
      intercom_user_jwt: token,
      api_base: "https://api-iam.eu.intercom.io",
    });
    // Called on boot with the initial count,
    // and again whenever the count changes.
    onUnreadCountChange((count: number) => {
      setUnreadCount(count);
    });
    return () => {
      // Tears down the Intercom instance, which also clears the
      // onUnreadCountChange callback registered above.
      shutdownIntercom();
    };
  }, [appId, userId, name, token]);

  return (
    <IntercomContext.Provider
      value={{ unreadCount, showIntercom, isPending, isError }}
    >
      {children}
    </IntercomContext.Provider>
  );
};
